import {
  Button,
  useRepository
} from "@city-sage/aurum-avis-labs-components";
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Calendar, MapPin, UserPlus, UserCheck, User } from 'lucide-react';
import { ItineraryVisibilityEnum } from '@city-sage/core-client';
import { QueryKeys } from '../../lib/QueryKeys';
import { ItineraryCard } from '../ItineraryCard';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import { Avatar as CustomAvatar } from '../Avatar';
import { useFollow } from '../../lib/useFollow';
import { ShareButton } from '../actions/ShareButton';
import { useFormattedDate } from '../../lib/dateUtils';
import CompactHeader from '../CompactHeader';

// Stats Component
interface Stats {
  tours: number;
  followers: number;
  following: number;
}

interface MobileProfileStatsProps {
  stats: Stats;
}

function MobileProfileStats({ stats }: MobileProfileStatsProps) {
  const { t } = useTranslation();
  
  const statItems = [
    { label: t('pages.profile.stats.tours'), value: stats.tours },
    { label: t('pages.profile.stats.followers'), value: stats.followers },
    { label: t('pages.profile.stats.following'), value: stats.following },
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  return (
    <div className="bg-white mx-4 -mt-6 relative z-10 rounded-2xl shadow-sm">
      <div className="grid grid-cols-4 divide-x divide-gray-100">
        {statItems.map((item, index) => (
          <button
            key={item.label}
            className="p-4 text-center hover:bg-gray-50 transition-colors first:rounded-l-2xl last:rounded-r-2xl"
          >
            <div className="text-xl font-bold text-gray-900">{formatNumber(item.value)}</div>
            <div className="text-xs text-gray-600 font-medium">{item.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PublicProfilePage() {
  const { userId } = useParams({ from: '/users/$userId' });
  const { t } = useTranslation();
  const { formatDateShort } = useFormattedDate();
  const navigate = useNavigate();
  
  const { api } = useRepository();
  
  // Use the follow hook
  const {
    isFollowing,
    isOwnProfile,
    canFollow,
    isPending: isToggling,
    followerCount,
    followingCount,
    toggleFollow,
    currentAccount
  } = useFollow({ userId });

  // Redirect to /profile if viewing own profile
  useEffect(() => {
    if (isOwnProfile) {
      navigate({ to: '/profile' });
    }
  }, [isOwnProfile, navigate]);

  // Get the user's account data
  const { data: userAccount, isLoading: isLoadingAccount, error } = useQuery({
    queryKey: QueryKeys.account.details(userId!),
    queryFn: () => api.findOneAccount({ id: userId }),
    enabled: !!userId && !isOwnProfile
  });

  // Get user's public itineraries with infinite scroll
  const {
    data: itinerariesData,
    isLoading: isLoadingItineraries,
    isError: itinerariesIsError,
    error: itinerariesError,
    fetchNextPage: fetchNextItineraries,
    hasNextPage: hasNextItineraries,
    isFetchingNextPage: isFetchingNextItineraries
  } = useInfiniteQuery({
    queryKey: ['itineraries', 'public', userId],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await api.findAllItineraryRaw({ 
        limit: 12,
        page: pageParam,
        filter: JSON.stringify({
          accountId: userId,
          visibility: ItineraryVisibilityEnum.Public
        })
      })
      return await res.value()
    },
    enabled: !!userId && !isOwnProfile,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < 12) return undefined
      return (allPages?.length ?? 0) + 1
    },
    initialPageParam: 1,
  });

  // Flatten infinite query results
  const itineraries = itinerariesData?.pages?.flat() || [];

  // Stats with real follow data
  const stats = {
    tours: itineraries?.length || 0,
    followers: followerCount,
    following: followingCount,
  };

  if (error) {
    return (
      <div className="min-h-dvh bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto flex items-center justify-center">
            <MapPin className="h-8 w-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{t('pages.publicProfile.userNotFound')}</h2>
          <p className="text-gray-600">{t('pages.publicProfile.userNotFoundDescription')}</p>
          <Link to="/">
            <Button>{t('common.actions.goHome')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoadingAccount) {
    return (
      <div className="min-h-dvh bg-gray-50">
        <div className="animate-pulse">
          <div className="h-14 bg-white border-b border-gray-100"></div>
          <div className="bg-background px-4 py-8">
            <div className="text-center space-y-4">
              <div className="w-24 h-24 bg-white/30 rounded-full mx-auto"></div>
              <div className="space-y-2">
                <div className="h-6 bg-white/30 rounded w-48 mx-auto"></div>
                <div className="h-4 bg-white/30 rounded w-32 mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-gray-50 pb-20">
      {/* Header */}
        <CompactHeader
          backTo="historyback"
          icon={User}
          title={`@${userAccount?.userName || ''}`}
          subtitle="Profile"
        />

      {/* Profile Header */}
      <div className="bg-primary px-4 py-8 text-white">
        <div className="text-center space-y-4">
          {/* Avatar */}
          <div className="relative inline-block">
            <div className="relative h-24 w-24 mx-auto overflow-hidden rounded-full border-4 border-white/30 shadow-lg">
               {userAccount ? (
                 <CustomAvatar
                   account={userAccount}
                   className="w-full h-full"
                   textClassName="text-2xl text-white font-bold bg-white/20"
                 />
               ) : (
                 <div className="w-full h-full bg-white/20 flex items-center justify-center">
                   <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                 </div>
               )}
             </div>
          </div>

          {/* User Info */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">@{userAccount?.userName}</h1>
            <div className="flex items-center justify-center gap-2 text-primary-foreground/80">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">Joined {userAccount?.createdAt ? formatDateShort(userAccount.createdAt) : ''}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-2 flex items-center justify-center gap-3">
            {canFollow && (
              <Button
                onClick={toggleFollow}
                disabled={isToggling}
                variant={isFollowing ? "secondary" : "default"}
                className={`min-w-[120px] ${
                  isFollowing 
                    ? 'bg-white/20 hover:bg-white/30 text-white border-white/30' 
                    : 'bg-white text-primary hover:bg-white/90'
                }`}
              >
                {isToggling ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {isFollowing ? (
                      <>
                        <UserCheck className="h-4 w-4 mr-2" />
                        {t('pages.publicProfile.following')}
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        {t('pages.publicProfile.follow')}
                      </>
                    )}
                  </>
                )}
              </Button>
            )}
            
            <ShareButton
              url={window.location.href}
              title={`${userAccount?.userName}'s Profile`}
              description={`Check out @${userAccount?.userName}'s travel profile on CitySage`}
              hashtags={['CitySage', 'Travel', 'Profile', userAccount?.userName || '']}
              variant="light"
              size="md"
              shareMethod="native"
            />
          </div>

        </div>
      </div>

      {/* Stats */}
      <MobileProfileStats stats={stats} />

      {/* About Me Section */}
      {userAccount?.aboutMe && (
        <div className="bg-white mx-4 mt-4 rounded-2xl shadow-sm">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('pages.profile.about.title')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {userAccount.aboutMe}
            </p>
          </div>
        </div>
      )}

      {/* Public Tours Section */}
      <div className="px-4 mt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
            <MapPin className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t('pages.publicProfile.publicTours', { username: userAccount?.userName })}
          </h2>
        </div>

        {isLoadingItineraries ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-2xl shadow-sm p-4">
                <div className="flex gap-3">
                  <div className="w-16 h-16 bg-gray-300 rounded-xl"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : itineraries && itineraries.length > 0 ? (
          <>
            <div className="space-y-4">
              {itineraries.map((itinerary) => (
                <ItineraryCard 
                  key={itinerary.id} 
                  itinerary={itinerary}
                  link={`/itineraries/${itinerary.id}`} 
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasNextItineraries && (
              <div className="flex justify-center pt-6">
                <Button
                  variant="outline"
                  onClick={() => fetchNextItineraries()}
                  disabled={isFetchingNextItineraries}
                  className="min-w-[120px]"
                >
                  {isFetchingNextItineraries ? t('common.loading') : t('common.loadMore')}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto flex items-center justify-center">
                <MapPin className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('pages.publicProfile.noPublicTours.title')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('pages.publicProfile.noPublicTours.description', { username: userAccount?.userName })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 